import axiosConfig from '../utils/axiosConfig';

export interface ReorderQuantityWarehouse {
  _id: string;
  warehouse: {
    _id: string;
    name: string;
    location?: string;
  };
  warehouseName: string;
  reorderQuantity: number;
  currentStock?: number; // الكمية الحالية في المخزون
  lastUpdated: string;
}

export interface ReorderQuantity {
  _id: string;
  product: {
    _id: string;
    name: string;
    status: string;
  };
  productName: string;
  variantId: string;
  variantSerialNumber: number;
  variantAttributes: Record<string, string>;
  barcode: string;
  sku?: string;
  unit: string;
  categories: Array<{
    _id: string;
    name: string;
  }>;
  warehouseReorderQuantities: ReorderQuantityWarehouse[];
  createdAt: string;
  updatedAt: string;
}

export interface ReorderQuantityFilters {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  warehouse?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ReorderQuantityResponse {
  success: boolean;
  data: ReorderQuantity[];
  pagination: {
    current: number;
    pages: number;
    total: number;
    limit: number;
  };
}

export interface ReorderQuantityUpdate {
  reorderQuantityId: string;
  warehouseId: string;
  newQuantity: number;
}

export interface UpdateReorderQuantitiesRequest {
  updates: ReorderQuantityUpdate[];
}

export interface UpdateReorderQuantitiesResponse {
  success: boolean;
  message: string;
  results: Array<{
    reorderQuantityId: string;
    warehouseId: string;
    newQuantity: number;
    success: boolean;
  }>;
  errors?: Array<{
    update: ReorderQuantityUpdate;
    error: string;
  }>;
}

export interface Warehouse {
  _id: string;
  name: string;
  location?: string;
}

export interface Category {
  _id: string;
  name: string;
}

export interface WarehousesResponse {
  success: boolean;
  data: Warehouse[];
}

export interface CategoriesResponse {
  success: boolean;
  data: Category[];
}

export interface SyncResponse {
  success: boolean;
  message: string;
  syncedCount: number;
  createdCount: number;
}

class ReorderQuantityService {
  private baseURL = '/reorder-quantities';

  // Get all reorder quantities with filtering and pagination
  async getReorderQuantities(filters: ReorderQuantityFilters = {}): Promise<ReorderQuantityResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.warehouse) params.append('warehouse', filters.warehouse);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

      const response = await axiosConfig.get(`${this.baseURL}?${params.toString()}`, {
        headers: {
          'x-permission-context': 'reorder-quantities-report'
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching reorder quantities:', error);
      throw new Error(error.response?.data?.message || 'خطأ في استرجاع كميات إعادة الطلب');
    }
  }

  // Get reorder quantity by ID
  async getReorderQuantityById(id: string): Promise<{ success: boolean; data: ReorderQuantity }> {
    try {
      const response = await axiosConfig.get(`${this.baseURL}/${id}`);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching reorder quantity:', error);
      throw new Error(error.response?.data?.message || 'خطأ في استرجاع كمية إعادة الطلب');
    }
  }

  // Update multiple reorder quantities
  async updateReorderQuantities(updates: ReorderQuantityUpdate[]): Promise<UpdateReorderQuantitiesResponse> {
    try {
      const response = await axiosConfig.put(this.baseURL, { updates });
      return response.data;
    } catch (error: any) {
      console.error('Error updating reorder quantities:', error);
      throw new Error(error.response?.data?.message || 'خطأ في تحديث كميات إعادة الطلب');
    }
  }

  // Sync reorder quantities with products and warehouses
  async syncReorderQuantities(): Promise<SyncResponse> {
    try {
      const response = await axiosConfig.post(`${this.baseURL}/sync`);
      return response.data;
    } catch (error: any) {
      console.error('Error syncing reorder quantities:', error);
      throw new Error(error.response?.data?.message || 'خطأ في مزامنة كميات إعادة الطلب');
    }
  }

  // Get warehouses for dropdown
  async getWarehouses(): Promise<WarehousesResponse> {
    try {
      const response = await axiosConfig.get(`${this.baseURL}/warehouses`, {
        headers: {
          'x-permission-context': 'reorder-quantities-report'
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching warehouses:', error);
      throw new Error(error.response?.data?.message || 'خطأ في استرجاع المستودعات');
    }
  }

  // Get categories for dropdown
  async getCategories(): Promise<CategoriesResponse> {
    try {
      const response = await axiosConfig.get('/categories', {
        headers: {
          'x-permission-context': 'reorder-quantities-report'
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      throw new Error(error.response?.data?.message || 'خطأ في استرجاع التصنيفات');
    }
  }

  // Export current quantities to Excel
  async exportCurrentQuantities(): Promise<any> {
    try {
      const response = await axiosConfig.get(`${this.baseURL}/export`);
      return response.data;
    } catch (error: any) {
      console.error('Error exporting quantities:', error);
      throw new Error(error.response?.data?.message || 'خطأ في تصدير الكميات');
    }
  }

  // Import updated quantities from Excel
  async importUpdatedQuantities(excelData: any[]): Promise<any> {
    try {
      const response = await axiosConfig.post(`${this.baseURL}/import`,
        { excelData },
        {
          headers: {
            'x-branch-id': localStorage.getItem('activeBranchId') || 'default'
          }
        }
      );
      return response.data;
    } catch (error: any) {
      console.error('Error importing quantities:', error);
      throw new Error(error.response?.data?.message || 'خطأ في استيراد الكميات');
    }
  }
}

export default new ReorderQuantityService();